import { bg1, bgPrimary } from '@osmosis/tailwind-ui/styles/background';
/* eslint-disable jsx-a11y/no-autofocus -- custom Modal prop focuses through react-focus-lock. */
import { text0, text3, text5 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { Checkbox } from '@osmosis/tailwind-ui/components/Checkbox';
import { Input } from '@osmosis/tailwind-ui/components/Input';

import React from 'react';
import Image from 'next/image';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import Modal from '../Modal';
import { FormattedMessage, useIntl } from 'react-intl';
import useModal from '../../hooks/useModal';
import Snackbar from '../Snackbar';

type EmbedVideoModalProps = {
  thumbnail: string;
  isVisible: boolean;
  hide: () => void;
  concept: string;
};

const inputsx = {
  fontSize: 16,
  fontWeight: 400,
  color: '#636363',
  border: 'none',
  outline: 'none',
  borderRadius: 0,
  borderBottom: '2px solid #e3e3e3',
  borderTopLeftRadius: 5,
  borderTopRightRadius: 5,
  transition: 'all 0.1s ease-in-out',
  padding: 10,
  width: 196,
  marginTop: '5px',
};

const OptionalHeader = () => (
  <span className={`font-normal ml-[5px] sm:text-base text-sm ${text5}`}>
    <FormattedMessage
      id="learn.share.embed.width.optional"
      defaultMessage="(optional)"
    />
  </span>
);

/**
 * EmbedVideoModal.
 */
export default function EmbedVideoModal({
  thumbnail,
  isVisible,
  hide,
  concept,
}: EmbedVideoModalProps): JSX.Element {
  const colors = useColors();
  const intl = useIntl();

  const [width, setWidth] = React.useState<string>('');
  const [height, setHeight] = React.useState<string>('');
  const [autoPlay, setAutoPlay] = React.useState<boolean>(false);

  const snackBar = useModal();

  const urlToEmbed = `https://osmosis.org/embed/${concept.replace(/\s/g, '_')}`;

  const urlWidth = width ? `width = ${width}` : '';
  const urlHeight = height ? `height = ${height} ` : '';
  const urlWithAutoPlay = autoPlay
    ? `${urlToEmbed}?autoplay = ${autoPlay} `
    : urlToEmbed;

  const options = ['allowfullscreen', urlWidth, urlHeight]
    .filter((option) => option)
    .join(' ');

  const embedUrl = `<iframe src = "${urlWithAutoPlay}" ${options} ></iframe> `;

  const handleCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(embedUrl).then(() => {
        snackBar.show();
        hide();
      });
    }
  };

  return (
    <>
      <Modal
        isVisible={isVisible}
        hide={hide}
        autoFocus
        _className="overflow-hidden max-h-[80vh]!"
      >
        <Box className={`p-5 max-w-[500px] h-[60vh] overflow-y-scroll`}>
          <Image alt="" src={thumbnail} width={490} height={238} />

          <H4 className={`text-lg my-5`}>
            <FormattedMessage
              id="learn.share.embed.video"
              defaultMessage="Embed Video"
            />
          </H4>

          <Box className={`p-2.5`}>
            <Flex className={`flex-nowrap rounded-[4px] overflow-hidden`}>
              <P
                className={`webkit-scrollbar overflow-y-hidden p-4 bg-gray-50 overflow-x-scroll basis-[85%]`}
              >
                {embedUrl}
              </P>
              <Button
                variant="transparent"
                data-testid="copy src"
                onClick={handleCopy}
                aria-label={intl.formatMessage({
                  id: 'learn.share.embed.copyCode',
                  defaultMessage: 'Copy embed code',
                })}
                className={`items-center justify-center cursor-pointer ${bg1} hover:bg-gray-200 basis-1/6 rounded-none`}
              >
                <Icon icon={faCopy} size="xl" />
              </Button>
            </Flex>
          </Box>
          <H3 className={`my-5`}>
            <FormattedMessage
              id="learn.share.embed.option"
              defaultMessage="Options"
            />
          </H3>

          <Box className={`my-4`}>
            <Checkbox
              data-testid="auto play"
              color={colors.primary}
              label={
                <H4>
                  <FormattedMessage
                    id="learn.share.embed.autoplay"
                    defaultMessage="Autoplay"
                  />
                </H4>
              }
              labelClassName="cursor-pointer"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAutoPlay(e.target.checked)
              }
            />
          </Box>

          <Flex className={`items-center justify-between`}>
            <Box>
              <label
                htmlFor="width"
                className="font-bold text-slate-800 sm:text-base text-sm"
              >
                <FormattedMessage
                  id="learn.share.embed.width"
                  defaultMessage="Width"
                />
              </label>
              <OptionalHeader />
              <Input
                data-testid="width"
                id="width"
                type="number"
                min="1"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setWidth(e.target.value)
                }
                className="focus:border-primary focus:border-2"
                style={{
                  ...inputsx,
                }}
              />
            </Box>

            <Box>
              <label
                htmlFor="width"
                className="font-bold text-slate-800 sm:text-base text-sm"
              >
                <FormattedMessage
                  id="learn.share.embed.height"
                  defaultMessage="Height"
                />
              </label>
              <OptionalHeader />
              <Input
                data-testid="height"
                id="height"
                type="number"
                min="1"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setHeight(e.target.value)
                }
                className="focus:border-primary focus:border-2"
                style={{
                  ...inputsx,
                }}
              />
            </Box>
          </Flex>
        </Box>

        <Flex
          style={{
            borderTop: '1px solid #e3e3e3',
          }}
          className={`p-[15px] items-center justify-center flex-wrap gap-4`}
        >
          <Button
            data-testid="cancel"
            onClick={hide}
            className={`w-full md:w-[auto] ${bg1} ${text3} rounded-[10rem] hover:bg-gray-200`}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            data-testid="copy"
            onClick={handleCopy}
            className={`w-full md:w-[auto] ${bgPrimary} ${text0} rounded-[10rem]`}
          >
            <FormattedMessage
              id="learn.share.embed.copy"
              defaultMessage="Copy code and close"
            />
          </Button>
        </Flex>
      </Modal>
      <Snackbar
        isVisible={snackBar.isVisible}
        hide={snackBar.hide}
        aria-live="polite"
        variant="positive"
        message={'Code copied to clipboard'}
      />
    </>
  );
}
